import React, { useState, useEffect, useCallback } from 'react';
import '../styles/UI.css';

/* ── Mobile detection hook ───────────────────────────────────────────────── */
export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

/* ── Badge ────────────────────────────────────────────────────────────────── */
const BADGE_COLORS = {
  green:  { bg: 'var(--green-bg)',  color: 'var(--green)'  },
  red:    { bg: 'var(--red-bg)',    color: 'var(--red)'    },
  amber:  { bg: 'var(--amber-bg)', color: 'var(--amber)'  },
  blue:   { bg: 'var(--blue-bg)',  color: 'var(--accent)'  },
  purple: { bg: 'var(--purple-bg)',color: 'var(--purple)'  },
  gray:   { bg: 'var(--bg3)',      color: 'var(--text2)'   },
};

export function Badge({ text, variant = 'gray', style: s }) {
  const c = BADGE_COLORS[variant] || BADGE_COLORS.gray;
  return (
    <span className="ui-badge" style={{ background: c.bg, color: c.color, ...s }}>
      {text || '—'}
    </span>
  );
}

/* ── Issue type badge ─────────────────────────────────────────────────────── */
const ISSUE_VARIANTS = {
  MISSING_GST:            'red',
  MISSING_NARRATION:      'gray',
  MISSING_PARTY:          'amber',
  UNBALANCED_VOUCHER:     'red',
  DUPLICATE_VOUCHER_NO:   'red',
  PAYMENT_WITHOUT_INVOICE:'amber',
  SALARY_NOT_BOOKED:      'amber',
  STATUTORY_UNPAID:       'red',
  OVERDUE_DEBTOR:         'amber',
  ADVANCE_NOT_ADJUSTED:   'blue',
  OVERDUE_CREDITOR:       'amber',
  EXCESS_PAYMENT:         'red',
  HIGH_VALUE_EXPENSE:     'purple',
  STALE_BANK_BALANCE:     'amber',
  INCORRECT_GST_RATE:     'red',
  MISSING_HSN_CODE:       'amber',
  INVALID_GSTIN:          'red',
  TDS_NOT_DEDUCTED:       'red',
  FUTURE_DATED_VOUCHER:   'amber',
  GST_CALCULATION_ERROR:  'red',
  MISSING_GSTIN_PURCHASE: 'amber',
  ABNORMAL_BALANCE:       'amber',
  ROUND_NUMBER_PAYMENT:   'amber',
};

export function IssueBadge({ type }) {
  return (
    <Badge
      text={(type || '').replace(/_/g,' ')}
      variant={ISSUE_VARIANTS[type] || 'gray'}
    />
  );
}

/* ── Tag ──────────────────────────────────────────────────────────────────── */
export function Tag({ children }) {
  if (!children || children === 'null' || children === 'undefined') {
    return <span className="ui-tag-empty">—</span>;
  }
  return <span className="ui-tag">{children}</span>;
}

/* ── Card ─────────────────────────────────────────────────────────────────── */
export function Card({ children, style: s, className }) {
  return (
    <div className={`ui-card${className ? ` ${className}` : ''}`} style={s}>
      {children}
    </div>
  );
}

export function CardHeader({ title, right }) {
  return (
    <div className="ui-card-header">
      <span className="ui-card-header-title">{title}</span>
      {right}
    </div>
  );
}

/* ── Stat card ────────────────────────────────────────────────────────────── */
export function StatCard({ label, value, accent, style: s }) {
  const colors = {
    red:   'var(--red)',
    green: 'var(--green)',
    amber: 'var(--amber)',
    blue:  'var(--accent)',
  };
  return (
    <div className="ui-stat-card" style={s}>
      <div className="ui-stat-label">{label}</div>
      <div
        className="ui-stat-value"
        style={accent ? { color: colors[accent] || accent } : undefined}
      >
        {value ?? '—'}
      </div>
    </div>
  );
}

/* ── Table — desktop table + mobile card layout ───────────────────────────── */
export function Table({ columns, rows, emptyMsg = 'No data' }) {
  const isMobile = useIsMobile();

  if (!rows || rows.length === 0) {
    return <div className="ui-table-empty">{emptyMsg}</div>;
  }

  // ── Mobile: render each row as a card ────────────────────────────────────
  if (isMobile) {
    return (
      <div className="ui-table-mobile">
        {rows.map((row, ri) => (
          <div key={ri} className="ui-table-mobile-row">
            {columns.map((col, ci) => {
              const value = col.render ? col.render(row) : (row[col.key] ?? '—');
              if (value === '—' || value === null || value === undefined) return null;
              return (
                <div key={ci} className="ui-table-mobile-field">
                  <span className="ui-table-mobile-label">{col.label}</span>
                  <span className="ui-table-mobile-value">{value}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  // ── Desktop: standard table ───────────────────────────────────────────────
  return (
    <div className="ui-table-wrap">
      <table className="ui-table">
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th
                key={i}
                className="ui-th"
                style={{ textAlign: col.right ? 'right' : 'left' }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="ui-tr">
              {columns.map((col, ci) => (
                <td
                  key={ci}
                  className={`ui-td${col.mono ? ' ui-td--mono' : ''}`}
                  style={{
                    textAlign: col.right ? 'right' : 'left',
                    color: col.color ? col.color(row) : undefined,
                    maxWidth: col.maxWidth || undefined,
                    overflow: col.maxWidth ? 'hidden' : undefined,
                    textOverflow: col.maxWidth ? 'ellipsis' : undefined,
                    whiteSpace: col.noWrap ? 'nowrap' : undefined,
                  }}
                >
                  {col.render ? col.render(row) : (row[col.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Search input — full width on mobile ─────────────────────────────────── */
export function SearchInput({ value, onChange, placeholder }) {
  const isMobile = useIsMobile();
  return (
    <input
      className="ui-search-input"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || 'Search...'}
      style={{ width: isMobile ? '100%' : 220 }}
    />
  );
}

/* ── Pill filter ──────────────────────────────────────────────────────────── */
export function Pill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`ui-pill${active ? ' ui-pill--active' : ''}`}
    >
      {label}
    </button>
  );
}

/* ── Loading skeleton ─────────────────────────────────────────────────────── */
export function Skeleton({ rows = 5 }) {
  return (
    <div className="ui-skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton ui-skeleton-row" style={{ opacity: 1 - i * 0.12 }} />
      ))}
    </div>
  );
}

/* ── Amount cell ──────────────────────────────────────────────────────────── */
export function AmountCell({ value, debit }) {
  const num = parseFloat(value);
  const isNeg = num < 0;
  const color = debit
    ? (isNeg ? 'var(--green)' : 'var(--red)')
    : (isNeg ? 'var(--red)'  : 'var(--green)');
  return (
    <span className="ui-amount-cell" style={{ color }}>
      {Math.abs(num).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2})}
    </span>
  );
}

/* ── Page wrapper with fade-in ────────────────────────────────────────────── */
export function PageWrap({ children }) {
  return <div className="fade-in ui-page-wrap">{children}</div>;
}

export function PageHeader({ title, sub }) {
  return (
    <div className="ui-page-header">
      <h1 className="ui-page-title">{title}</h1>
      {sub && <p className="ui-page-sub">{sub}</p>}
    </div>
  );
}

/* ── StatGrid — responsive columns ───────────────────────────────────────── */
export function StatGrid({ children, cols = 4 }) {
  const isMobile = useIsMobile();
  const effectiveCols = isMobile ? Math.min(2, cols) : cols;
  return (
    <div
      className="ui-stat-grid"
      style={{ gridTemplateColumns: `repeat(${effectiveCols},1fr)` }}
    >
      {children}
    </div>
  );
}

export function TwoCol({ children }) {
  const isMobile = useIsMobile();
  return (
    <div
      className="ui-two-col"
      style={{ gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}
    >
      {children}
    </div>
  );
}

/* ── Toast ────────────────────────────────────────────────────────────────── */

export function showToast(message, variant = 'green') {
  window.dispatchEvent(new CustomEvent('audit-pros-toast', { detail: { message, variant } }));
}

export function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((e) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, ...e.detail }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3200);
  }, []);

  useEffect(() => {
    window.addEventListener('audit-pros-toast', addToast);
    return () => window.removeEventListener('audit-pros-toast', addToast);
  }, [addToast]);

  if (!toasts.length) return null;
  return (
    <div className="ui-toast-container">
      {toasts.map(t => (
        <div key={t.id} className={`ui-toast ui-toast--${t.variant || 'green'}`}>
          {t.message}
        </div>
      ))}
    </div>
  );
}

/* ── BackButton ───────────────────────────────────────────────────────────── */

export function BackButton({ label, onClick }) {
  return (
    <button className="ui-back-btn" onClick={onClick}>
      ← {label}
    </button>
  );
}
